import {
    SidebarInset,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import {
    Card,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { TrendingDownIcon, TrendingUpIcon } from "lucide-react";

import { useTheme } from "@/layout/ThemeProvider";
import { useEffect, useState } from "react";
import ReservationCalendar from "@/rental/calendar/reservations-calendar";
import dataProvider from "@/dataProvider";
import { ChartAreaInteractive } from "@/components/chart-area-interactive";
import ReservationTimeline from "./ReservationTimeline";

export const Dashboard = () => {
    const { setTheme } = useTheme();
    const [totalReservations, setTotalReservations] = useState({ pending: 0, in_progress: 0, finished: 0 })
    const [profit, setProfit] = useState(0);
    const [reloadCounter, setReloadCounter] = useState(0);

    useEffect(() => {
        dataProvider.getList("reservations", {
            pagination: { page: 1, perPage: 10000 },
            sort: { field: 'reference', order: "ASC" },
            filter: { month: new Date().getMonth() + 1 }
        }).then(res => {
            res.data.map(item => {
                if (item.status === "pending") setTotalReservations(prev => {
                    return { ...prev, pending: prev.pending + 1 }
                })
                else if (item.status === "approved" && new Date(item.pickup_date) < new Date() && new Date(item.dropoff_date) > new Date()) setTotalReservations(prev => {
                    return { ...prev, in_progress: prev.in_progress + 1 }
                })
                else if (item.status === "approved" && new Date(item.dropoff_date) < new Date()) setTotalReservations(prev => {
                    return { ...prev, finished: prev.finished + 1 }
                })
            })

            const revenue = res.data.reduce((acc, item) => {
                if (item.status === "approved" && new Date(item.dropoff_date) < new Date()) {
                    return acc + (item.amount || 0);
                }
                return acc;
            }, 0);

            setProfit(revenue);
        })

        dataProvider.getList("invoices", {
            pagination: { page: 1, perPage: 10000 },
            sort: { field: 'reference', order: "ASC" },
            filter: { month: new Date().getMonth() + 1 }
        }).then(res => {
            let invoiceProfit = 0;
            res.data.map(item => {
                if (item.status == "Paid") invoiceProfit += item.total
            })
            setProfit(invoiceProfit)
        })
    }, [])

    return (
        <SidebarInset>
            <div className="flex flex-1 flex-col">
                <div className="@container/main flex flex-1 flex-col gap-2">
                    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
                        <div
                            className="*:data-[slot=card]:shadow-xs px-4 *:data-[slot=card]:bg-gradient-to-t 
                            *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4 grid grid-cols-1 gap-4 "
                        >
                            <Card className="@container/card">
                                <CardHeader className="relative">
                                    <CardDescription>Total Revenue</CardDescription>
                                    <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
                                        ${profit}
                                    </CardTitle>
                                    <div className="absolute right-4 top-4">
                                        <Badge
                                            variant="outline"
                                            className="flex gap-1 rounded-lg text-xs"
                                        >
                                            <TrendingUpIcon className="size-3" />
                                            +12.5%
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardFooter className="flex-col items-start gap-1 text-sm">
                                    <div className="line-clamp-1 flex gap-2 font-medium">
                                        Trending up this month <TrendingUpIcon className="size-4" />
                                    </div>
                                    <div className="text-muted-foreground">
                                        Visitors for the last 6 months
                                    </div>
                                </CardFooter>
                            </Card>
                            <Card className="@container/card">
                                <CardHeader className="relative">
                                    <CardDescription>New reservations</CardDescription>
                                    <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
                                        {totalReservations.pending}
                                    </CardTitle>
                                    <div className="absolute right-4 top-4">
                                        <Badge
                                            variant="outline"
                                            className="flex gap-1 rounded-lg text-xs"
                                        >
                                            <TrendingDownIcon className="size-3" />
                                            -20%
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardFooter className="flex-col items-start gap-1 text-sm">
                                    <div className="line-clamp-1 flex gap-2 font-medium">
                                        Down 20% this period <TrendingDownIcon className="size-4" />
                                    </div>
                                    <div className="text-muted-foreground">
                                        Acquisition needs attention
                                    </div>
                                </CardFooter>
                            </Card>
                            <Card className="@container/card">
                                <CardHeader className="relative">
                                    <CardDescription>Reservations in progress</CardDescription>
                                    <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
                                        {totalReservations.in_progress}
                                    </CardTitle>
                                    <div className="absolute right-4 top-4">
                                        <Badge
                                            variant="outline"
                                            className="flex gap-1 rounded-lg text-xs"
                                        >
                                            <TrendingUpIcon className="size-3" />
                                            +12.5%
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardFooter className="flex-col items-start gap-1 text-sm">
                                    <div className="line-clamp-1 flex gap-2 font-medium">
                                        Strong user retention <TrendingUpIcon className="size-4" />
                                    </div>
                                    <div className="text-muted-foreground">
                                        Engagement exceed targets
                                    </div>
                                </CardFooter>
                            </Card>
                            <Card className="@container/card">
                                <CardHeader className="relative">
                                    <CardDescription>Completed reservations</CardDescription>
                                    <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
                                        {totalReservations.finished}
                                    </CardTitle>
                                    <div className="absolute right-4 top-4">
                                        <Badge
                                            variant="outline"
                                            className="flex gap-1 rounded-lg text-xs"
                                        >
                                            <TrendingUpIcon className="size-3" />
                                            +4.5%
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardFooter className="flex-col items-start gap-1 text-sm">
                                    <div className="line-clamp-1 flex gap-2 font-medium">
                                        Steady performance <TrendingUpIcon className="size-4" />
                                    </div>
                                    <div className="text-muted-foreground">
                                        Meets growth projections
                                    </div>
                                </CardFooter>
                            </Card>
                        </div>
                        <div
                            className="*:data-[slot=card]:shadow-xs px-4 *:data-[slot=card]:bg-gradient-to-t 
                            *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card lg:px-6 grid md:grid-cols-2 gap-5"
                        >
                            <ReservationTimeline onSave={(updatedEvent) => {
                                setReloadCounter((prev) => prev + 1);
                            }} />

                            <div className="w-full place-self-stretch">
                                <ChartAreaInteractive />
                            </div>
                        </div>

                        <div className="grid place-items-start px-4 lg:px-6 gap-5">
                            <div className="max-h-full w-full ">
                                <Card className="p-4 max-h-full">
                                    <ReservationCalendar key={reloadCounter} />
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SidebarInset>
    );
};

export default Dashboard;
